import {
  BodyFilterType,
  FilterType,
  FilterValueTypes,
  TreeFilterType,
  ValueFilter,
} from './types'
import { JsonEditor, UpdateFunction } from 'json-edit-react'
import React, { useEffect, useState } from 'react'
import { Box, Button, MenuItem, Select, TextField } from '@mui/material'
import { useToggleState } from 'shared/hooks'
import { convertUserInputToFilterValues } from './utility'

export function BodyFilter({
  filter,
  onFiltersChanged,
}: {
  filter: TreeFilterType
  onFiltersChanged(filters: TreeFilterType): void
}) {
  const onAdd = ({
    key,
    values,
    type,
  }: {
    type: BodyFilterType
    key: string
    values: FilterValueTypes[]
  }) => {
    onFiltersChanged({
      ...filter,
      body: {
        ...filter.body,
        [key]: {
          type,
          value: values,
        },
      },
    })
  }

  return (
    <Box>
      <NewBodyFilter onAdd={onAdd} currentFilter={filter} />
      <BodyFilterDisplay filter={filter} onFiltersChanged={onFiltersChanged} />
    </Box>
  )
}

function BodyFilterDisplay({
  filter,
  onFiltersChanged,
}: {
  filter: TreeFilterType
  onFiltersChanged(filters: TreeFilterType): void
}) {
  const data = filter.body ?? {}
  if (!Object.keys(data).length) return null
  const onEditComplete: UpdateFunction = ({ newData }) => {
    onFiltersChanged({
      ...filter,
      body: newData as Record<string, ValueFilter>,
    })
    return true
  }

  return (
    <JsonEditor
      collapse={true}
      rootName="bodyFilter"
      data={data}
      showStringQuotes={false}
      enableClipboard={true}
      onUpdate={onEditComplete}
      restrictEdit={(p) => p.level !== 3}
      restrictAdd={(p) => p.level !== 2 || p.key !== 'value'}
      restrictDelete={(p) => ![1, 3].includes(p.level)}
      restrictTypeSelection={() => ['string', 'number', 'boolean']}
      defaultValue={''}
      customNodeDefinitions={[
        {
          condition(p) {
            return p.level === 3 && p.value instanceof RegExp
          },
          element: ({ data }) => {
            if (data instanceof RegExp) {
              return (
                <Box>
                  RegEx: {data.source} Opts: {data.flags}
                </Box>
              )
            }
            return <Box>{String(data)}</Box>
          },
        },
      ]}
    />
  )
}

function NewBodyFilter({
  currentFilter,
  onAdd,
}: {
  currentFilter: TreeFilterType
  onAdd: (p: {
    type: BodyFilterType
    key: string
    values: FilterValueTypes[]
  }) => void
}) {
  const [filterType, setFilterType] = useState<BodyFilterType>('equal')
  const [adding, toggleAdding] = useToggleState()
  const [key, setKey] = useState('')
  const [value, setValue] = useState('')

  const selfFilter = filterType === 'self'

  useEffect(() => {
    if (!selfFilter) return
    setValue('')
  }, [selfFilter])

  const doToggle = () => {
    setKey('')
    setValue('')
    toggleAdding()
  }

  const doAdd = () => {
    if (!key.trim()) return
    if (selfFilter) {
      onAdd({ key, values: [], type: filterType })
    }

    if (!value.trim()) return
    const values = convertUserInputToFilterValues(value)
    onAdd({ key, values, type: filterType })
    doToggle()
  }

  if (adding) {
    return (
      <Box>
        <Button onClick={doAdd}>Add</Button>
        <Button onClick={doToggle}>Cancel</Button>
        <TextField
          value={key}
          onChange={({ target: { value } }) => setKey(value)}
          label="Body Key"
        />
        <Select
          value={filterType}
          onChange={({ target: { value } }) =>
            setFilterType(value as BodyFilterType)
          }
        >
          <MenuItem value="equal">Equal</MenuItem>
          <MenuItem value="notEqual">NotEqual</MenuItem>
          {currentFilter?.filterType === FilterType.leaf && (
            <MenuItem value="self">SelfEqual</MenuItem>
          )}
        </Select>
        <TextField
          disabled={selfFilter}
          value={value}
          onChange={({ target: { value } }) => setValue(value)}
          sx={{ width: '270px' }}
          label={
            selfFilter
              ? 'Filtering on Existing Value'
              : 'Comma separated values'
          }
        />
      </Box>
    )
  }

  return (
    <Box>
      <Button onClick={doToggle}>Add Body Filter</Button>
    </Box>
  )
}
